import { SaveOutlined } from '@ant-design/icons';
import { Checkbox, Col, Form, Input, message, Modal, Row, Select } from 'antd';
import React, { useState } from 'react';
import ZTable, { IFilterItem, ITableRef, IColumnType } from '@/components/ZTable';
import { useTranslation } from 'react-i18next';
import { AccountNumberRegex, ModalWidth, VALIDATE_REQUIRED } from '@/config';
import CountryInput from '@/components/FormItems/CountryInput';
import { CompanyAccountType, PaymentPartnerKind } from '@/constants';
import BooleanTag from '@/components/app/BooleanTag';
import PaymentPartnerInput from '@/components/FormItems/PamentPartnerInput';
import { useGetCompanyPaymentPartners } from '@/hooks/data/masterDataHook';
import CreateBtn from '@/components/app/CreateBtn';
import { CompanyAccountDto } from '@/__generated';
import { apiSdk } from '@/httpClient';

const columns: IColumnType<CompanyAccountDto>[] = [
  {
    title: 'base.Country',
    dataIndex: 'countryName',
  },
  {
    title: 'companySettingsPage_AccountType',
    dataIndex: 'accountType',
    render: v => CompanyAccountType.__getValue(v),
  },
  {
    title: 'companySettingsPage_OperatorBank',
    dataIndex: 'paymentPartnerName',
  },
  {
    title: 'companySettingsPage_AccountNumber',
    dataIndex: 'accountNumber',
  },
  {
    title: 'companySettingsPage_AccountOwner',
    dataIndex: 'accountOwner',
  },
  {
    title: 'base.IsActive',
    dataIndex: 'isActive',
    render: v => <BooleanTag value={v} />,
  },
];

const filters: Array<IFilterItem> = [
  { type: 'TEXT', name: 'accountOwner', label: 'companySettingsPage_AccountOwner' },
  { type: 'TEXT', name: 'accountNumber', label: 'companySettingsPage_AccountNumber' },
  { type: 'LIST', name: 'accountType', label: 'companySettingsPage_AccountType', listData: CompanyAccountType.__getSelectOptions() },
];

const AccountTab = () => {
  const { t } = useTranslation();
  const zTb = React.useRef<ITableRef>();
  const [form] = Form.useForm();
  const [formVisible, setFormVisible] = useState(false);
  const [isNewForm, setIsNewForm] = useState(false);
  const [loading, setLoading] = useState(false);
  const createEntity = values => {
    setLoading(true);
    apiSdk.CompanyAccountService.create({ body: values })
      .then(response => {
        if (response?.success) {
          setFormVisible(false);
          zTb.current?.reload();
        } else {
          if (response.message) {
            message.error(t(response.message || ''));
          }
        }
      })
      .catch(error => {
        message.error(error);
      })
      .finally(() => {
        setLoading(false);
      });
  };
  const updateEntity = values => {
    setLoading(true);
    apiSdk.CompanyAccountService.update({ body: values })
      .then(response => {
        if (response?.success) {
          setFormVisible(false);
          zTb.current?.reload();
        } else {
          if (response.message) {
            message.error(t(response.message || ''));
          }
        }
      })
      .catch(error => {
        message.error(error);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const paymentPartnerType = Form.useWatch('paymentPartnerType', form);
  // Le compte est ouvert dans le pays de la compagnie : seuls ses partenaires sont proposés.
  const companyCountryId = useGetCompanyPaymentPartners().data?.countryId;

  return (
    <div>
      <ZTable
        columns={columns}
        primaryKey="id"
        tableRef={zTb}
        url="/commons/company-accounts/get-company-accounts"
        bordered
        order_by="createdOn"
        order_by_asc={true}
        allowEdit={true}
        onEdit={record => {
          setIsNewForm(false);
          setFormVisible(true);
          form.setFieldsValue({
            ...record,
          });
        }}
        allowDelete={true}
        delete_url="/commons/company-accounts/delete"
        filters={filters}
        searchBtnAlignLeft
        extraBtns={
          <CreateBtn
            onClick={() => {
              setIsNewForm(true);
              setFormVisible(true);
              form.resetFields();
              form.setFieldsValue({ countryId: companyCountryId });
            }}
          />
        }
      ></ZTable>
      {formVisible ? (
        <Modal
          visible={formVisible}
          maskClosable={false}
          width={ModalWidth.small}
          className="nta-custom-form"
          title={isNewForm ? t('companySettingsPage_AddAccount') : t('companySettingsPage_UpdateAccount')}
          forceRender={true}
          okText={t('save')}
          cancelText={t('cancel')}
          okButtonProps={{ icon: <SaveOutlined />, className: 'save-btn', loading: loading }}
          onCancel={() => {
            form.resetFields();
            setFormVisible(false);
          }}
          onOk={() => {
            form
              .validateFields()
              .then(values => {
                if (values.id) {
                  updateEntity(values);
                } else {
                  createEntity(values);
                }
              })
              .catch(info => {});
          }}
        >
          <Form form={form} size="middle" layout="horizontal" labelAlign="right" labelCol={{ md: 10 }}>
            <Form.Item name="id" className="hidden">
              <Input type="hidden" />
            </Form.Item>
            <Row gutter={6}>
              <Col md={24}>
                <Form.Item label={t('base.Country')} name="countryId" rules={[{ required: true, message: VALIDATE_REQUIRED }]}>
                  <CountryInput disabled />
                </Form.Item>
                <Form.Item
                  label={t('companySettingsPage_AccountEntity')}
                  name="paymentPartnerType"
                  rules={[{ required: true, message: VALIDATE_REQUIRED }]}
                >
                  <Select
                    options={PaymentPartnerKind.__getSelectOptions()}
                    onChange={() => {
                      form.setFieldValue('paymentPartnerId', null);
                    }}
                  />
                </Form.Item>
                <Form.Item
                  label={t('companySettingsPage_OperatorBank')}
                  name="paymentPartnerId"
                  rules={[{ required: true, message: VALIDATE_REQUIRED }]}
                >
                  <PaymentPartnerInput partnerType={paymentPartnerType} />
                </Form.Item>
                <Form.Item label={t('companySettingsPage_AccountOwner')} name="accountOwner" rules={[{ required: true, message: VALIDATE_REQUIRED }]}>
                  <Input />
                </Form.Item>
                <Form.Item
                  label={t('companySettingsPage_AccountNumber')}
                  name="accountNumber"
                  rules={[
                    { required: true, message: VALIDATE_REQUIRED },
                    {
                      pattern: new RegExp(AccountNumberRegex),
                      message: t('companySettingsPage_AccountNumberMustBeNumer'),
                    },
                  ]}
                >
                  <Input />
                </Form.Item>
                <Form.Item label={t('companySettingsPage_AccountType')} name="accountType" rules={[{ required: true, message: VALIDATE_REQUIRED }]}>
                  <Select options={CompanyAccountType.__getSelectOptions()} />
                </Form.Item>
                <Form.Item name="isActive" valuePropName="checked" label="  " colon={false}>
                  <Checkbox>{t('base.IsActive')}</Checkbox>
                </Form.Item>
              </Col>
            </Row>
          </Form>
        </Modal>
      ) : null}
    </div>
  );
};
export default AccountTab;
